import { useEffect, useMemo, useState } from "react";
import {
    Loader2,
    Trash2,
    RefreshCcw,
    Search,
    Image as ImageIcon,
    Archive as ArchiveIcon,
    FileVideo,
    FileAudio,
    FileText,
    File as FileIcon,
    Copy,
    ExternalLink,
    HardDrive,
    AlertTriangle,
} from "lucide-react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";

/** Icon per file kind (as classified by the backend `_guess_kind`). */
const KIND_ICONS = {
    image: ImageIcon,
    video: FileVideo,
    audio: FileAudio,
    archive: ArchiveIcon,
    doc: FileText,
    other: FileIcon,
};

const KIND_LABELS = {
    image: "Obrazy",
    video: "Wideo",
    audio: "Dźwięk",
    archive: "Archiwa",
    doc: "Dokumenty",
    other: "Inne",
};

function fmtBytes(n) {
    if (!n) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso) {
    if (!iso) return "-";
    try {
        return new Date(iso).toLocaleString("pl-PL", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

const PAGE_SIZE = 40;

/**
 * Admin Media Manager
 * ────────────────────
 * Grid of every file uploaded to GridFS. Features:
 *   • filter by kind (image/video/…)   • server-side substring search on filename
 *   • paginated (40 per page)          • click a tile to preview / copy URL / delete
 *   • soft delete refuses if the file is referenced elsewhere - user can force
 *   • header shows total files + total bytes + per-kind counters
 */
export default function MediaManager() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [bytesTotal, setBytesTotal] = useState(0);
    const [byKind, setByKind] = useState({});
    const [kind, setKind] = useState("");
    const [q, setQ] = useState("");
    const [skip, setSkip] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams();
            params.set("limit", String(PAGE_SIZE));
            params.set("skip", String(skip));
            if (kind) params.set("kind", kind);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/admin/media?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
            setBytesTotal(data.bytes_total || 0);
            setByKind(data.by_kind || {});
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kind, skip]);

    const filteredKinds = useMemo(
        () => Object.entries(byKind).sort((a, b) => b[1].count - a[1].count),
        [byKind]
    );

    const doDelete = async (item, force = false) => {
        const label = item.filename || item.id;
        if (
            !window.confirm(
                force
                    ? `USUŃ WYMUSZONO plik "${label}"? Może to zepsuć powiązane sekcje.`
                    : `Usunąć plik "${label}"? Tego nie można cofnąć.`
            )
        )
            return;
        try {
            const url = force
                ? `/admin/media/${item.id}/force`
                : `/admin/media/${item.id}`;
            await api.delete(url);
            setSelected(null);
            await load();
        } catch (e) {
            const detail = e.response?.data?.detail;
            // If referenced elsewhere, offer force delete
            if (e.response?.status === 409 && !force) {
                if (window.confirm(`${detail}\n\nCzy usunąć mimo to?`)) {
                    return doDelete(item, true);
                }
                return;
            }
            setError(formatApiErrorDetail(detail) || e.message);
        }
    };

    const copyUrl = async (item) => {
        const abs = resolveMediaUrl(item.url);
        try {
            await navigator.clipboard.writeText(abs);
        } catch {
            /* older browsers */
        }
    };

    return (
        <div className="space-y-6" data-testid="media-manager">
            {/* Header + summary */}
            <div className="panel-yellow p-5 flex items-center gap-4 flex-wrap">
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--primary)]/20 text-[color:var(--primary)]">
                    <HardDrive size={18} />
                </div>
                <div className="flex-1 min-w-[180px]">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
                        Menedżer mediów
                    </div>
                    <div className="text-white text-sm">
                        {total.toLocaleString("pl-PL")} plików · {fmtBytes(bytesTotal)} łącznie
                    </div>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="btn-ghost text-xs shrink-0"
                    data-testid="media-refresh"
                >
                    <RefreshCcw size={12} className={loading ? "animate-spin" : ""} />
                    Odśwież
                </button>
            </div>

            {/* Filters bar */}
            <div className="flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-1 p-1 rounded-md border border-white/10 bg-[color:var(--bg-surface)]">
                    <FilterChip
                        active={kind === ""}
                        onClick={() => {
                            setKind("");
                            setSkip(0);
                        }}
                        label="Wszystkie"
                        count={total}
                    />
                    {filteredKinds.map(([k, v]) => {
                        const Icon = KIND_ICONS[k] || FileIcon;
                        return (
                            <FilterChip
                                key={k}
                                active={kind === k}
                                onClick={() => {
                                    setKind(k);
                                    setSkip(0);
                                }}
                                icon={Icon}
                                label={KIND_LABELS[k] || k}
                                count={v.count}
                            />
                        );
                    })}
                </div>
                <form
                    className="relative flex-1 min-w-[200px] max-w-sm ml-auto"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setSkip(0);
                        load();
                    }}
                >
                    <Search
                        size={13}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
                    />
                    <input
                        className="input pl-8"
                        placeholder="Szukaj po nazwie…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="media-search"
                    />
                </form>
            </div>

            {error && (
                <div className="text-xs text-red-400 flex items-center gap-2" data-testid="media-error">
                    <AlertTriangle size={13} />
                    {error}
                </div>
            )}

            {/* Grid */}
            {loading && items.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-[color:var(--text-secondary)]">
                    <Loader2 className="animate-spin" />
                </div>
            ) : items.length === 0 ? (
                <div className="p-10 text-center text-sm text-[color:var(--text-muted)] border border-dashed border-white/10 rounded-md">
                    Brak plików w tej kategorii.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {items.map((f) => (
                        <MediaTile
                            key={f.id}
                            file={f}
                            onClick={() => setSelected(f)}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between text-xs text-[color:var(--text-secondary)]">
                <div>
                    Pokazuję {items.length ? skip + 1 : 0}–{skip + items.length} z{" "}
                    {total.toLocaleString("pl-PL")}
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn-ghost text-xs"
                        onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                        disabled={skip === 0}
                    >
                        ← Poprzednia
                    </button>
                    <button
                        className="btn-ghost text-xs"
                        onClick={() => setSkip(skip + PAGE_SIZE)}
                        disabled={skip + items.length >= total}
                    >
                        Następna →
                    </button>
                </div>
            </div>

            {/* Preview modal */}
            {selected && (
                <MediaPreviewModal
                    file={selected}
                    onClose={() => setSelected(null)}
                    onDelete={() => doDelete(selected)}
                    onCopy={() => copyUrl(selected)}
                />
            )}
        </div>
    );
}

function FilterChip({ active, onClick, icon: Icon, label, count }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[10px] uppercase tracking-[0.15em] transition ${
                active
                    ? "bg-[color:var(--primary)] text-[#040914] font-bold"
                    : "text-[color:var(--text-secondary)] hover:text-white hover:bg-white/5"
            }`}
        >
            {Icon && <Icon size={11} />}
            <span>{label}</span>
            <span className={active ? "opacity-70" : "opacity-50"}>({count})</span>
        </button>
    );
}

function MediaTile({ file, onClick }) {
    const Icon = KIND_ICONS[file.kind] || FileIcon;
    const isImage = file.kind === "image";
    return (
        <button
            onClick={onClick}
            className="group relative aspect-square rounded-md overflow-hidden border border-white/10 bg-[color:var(--bg-surface)] hover:border-[color:var(--primary)]/60 transition"
            title={file.filename}
            data-testid={`media-tile-${file.id}`}
        >
            {isImage ? (
                <img
                    src={resolveMediaUrl(file.url)}
                    alt={file.filename}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[color:var(--text-secondary)]">
                    <Icon size={36} strokeWidth={1.4} />
                </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2 text-left">
                <div className="text-[10px] text-white truncate">{file.filename}</div>
                <div className="text-[9px] text-[color:var(--text-muted)] flex items-center gap-1.5">
                    <span>{fmtBytes(file.size)}</span>
                    {file.saved_pct > 0 && (
                        <span className="text-emerald-400">−{file.saved_pct}%</span>
                    )}
                </div>
            </div>
        </button>
    );
}

function MediaPreviewModal({ file, onClose, onDelete, onCopy }) {
    const [copied, setCopied] = useState(false);
    const isImage = file.kind === "image";
    return (
        <div
            className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="media-preview"
        >
            <div
                className="max-w-3xl w-full bg-[color:var(--bg-surface)] rounded-lg border border-white/10 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-4 px-5 py-3 border-b border-white/10">
                    <div className="flex-1 min-w-0">
                        <div className="text-white text-sm truncate">{file.filename}</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-secondary)]">
                            {file.content_type} · {fmtBytes(file.size)} · {fmtDate(file.upload_date)}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="btn-ghost text-xs"
                        data-testid="media-preview-close"
                    >
                        Zamknij
                    </button>
                </div>
                <div className="p-4 flex items-center justify-center bg-black/40 min-h-[240px] max-h-[60vh]">
                    {isImage ? (
                        <img
                            src={resolveMediaUrl(file.url)}
                            alt={file.filename}
                            className="max-w-full max-h-[60vh] object-contain"
                        />
                    ) : (
                        <div className="text-center text-[color:var(--text-secondary)] py-16">
                            <FileIcon size={64} strokeWidth={1.2} className="mx-auto mb-3 opacity-60" />
                            <div className="text-sm">{file.content_type || "Plik binarny"}</div>
                            <a
                                href={resolveMediaUrl(file.url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[color:var(--primary)] text-xs mt-2 inline-flex items-center gap-1"
                            >
                                Pobierz <ExternalLink size={11} />
                            </a>
                        </div>
                    )}
                </div>
                <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-t border-white/10">
                    <code className="text-[11px] font-mono text-[color:var(--text-secondary)] truncate max-w-[300px]">
                        {file.url}
                    </code>
                    <div className="ml-auto flex gap-2">
                        <button
                            onClick={async () => {
                                await onCopy();
                                setCopied(true);
                                setTimeout(() => setCopied(false), 1500);
                            }}
                            className="btn-ghost text-xs"
                            data-testid="media-copy-url"
                        >
                            <Copy size={12} />
                            {copied ? "Skopiowano" : "Kopiuj URL"}
                        </button>
                        <a
                            href={resolveMediaUrl(file.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost text-xs"
                        >
                            <ExternalLink size={12} />
                            Otwórz
                        </a>
                        <button
                            onClick={onDelete}
                            className="btn-ghost text-xs text-red-400 hover:text-red-300"
                            data-testid="media-delete"
                        >
                            <Trash2 size={12} />
                            Usuń
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
